import React, { useState, useEffect } from "react";
import { Table, Form, Card, Button, Col, Row } from "react-bootstrap";
import { FileMinus, Trash, Plus, Save, X } from "react-feather";
import api from "../../../../../service/api";
import { SnackbarAlert } from "../../../../../components/SnackbarAlert";
import { useNavigate } from "react-router-dom";
import { Controller } from "react-hook-form";
import { DeleteModal } from "../../../../../components/DeleteModal";

const ContactSection = ({
    contacts,
    permission,
    control,
    setValue,
    selectedCompany,
    getCompanyData,
    quotation,
    defaultContactData
}) => {
    const [editedContactId, setEditedContactId] = useState(null);
    const [editedContactData, setEditedContactData] = useState(null);
    const [newContact, setNewContact] = useState({
        name: "",
        contact_number: "",
        email: "",
    });
    const [modalDelete, setModalDelete] = useState({
        values: [],
        notifMsg: "",
        open: false,
        severity: "danger",
    });
    const [isAddingContact, setIsAddingContact] = useState(false); // Track whether adding a new contact
    const [notif, setNotif] = useState({
        notifMsg: "",
        open: false,
        severity: "success",
    });

    const handleEditClick = (contactData) => {
        setEditedContactId(contactData.id);
        setEditedContactData(contactData);
    };

    const handleSaveClick = async (contactId) => {  
        try {
            const response = await api.post(
                `tms/contact-persons-edit/${contactId}`,
                editedContactData
            );
            if (response.status === "ok") {
                setEditedContactId(null);
                await getCompanyData(selectedCompany);
                setNotif({
                    notifMsg: "Contact person updated successfully",
                    open: true,
                    severity: "success",
                });
            } else {
                setNotif({
                    notifMsg: response.message,
                    open: true,
                    severity: "danger",
                });
            }
        } catch (error) {
            if (error.response) {
                if (error.response.status === 422) {
                        // Extract validation error messages
                        const errors = error.response.data.errors || {};
                        const errorMessages = Object.values(errors).flat().join(", ");
    
                        setNotif({
                            notifMsg: `Validation failed: ${errorMessages}`,
                            open: true,
                            severity: "warning",
                        });
                        return;
                    }
                }
                else{
                    setNotif({
                        notifMsg: "Something went wrong with the server",
                        open: true,
                        severity: "danger",
                    });
                }
        }
    };

    const handleCancelClick = () => {
        setEditedContactId(null);
        setEditedContactData(null);
    };

    const handleNewContactChange = (field, value) => {
        setNewContact({
            ...newContact,
            [field]: value,
        });
    };

    const handleAddNewContact = async () => {
        try {
            // Assuming company_id is known or stored somewhere
            const companyId = selectedCompany; // Replace with the actual company_id value

            // Append company_id to newContact
            const contactWithCompanyId = {
                ...newContact,
                company_id: companyId,
            };

            const response = await api.post(
                "tms/contact-persons",
                contactWithCompanyId
            );
            if (response.status === "ok") {
                await getCompanyData(selectedCompany);
                setNewContact({
                    name: "",
                    contact_number: "",
                    email: "",
                });
                setNotif({
                    notifMsg: "New contact person added successfully",
                    open: true,
                    severity: "success",
                });
                setIsAddingContact(false); // Hide the input table after adding a contact
                // You can refresh the contact list or perform other actions here
            } else {
                setNotif({
                    notifMsg: response.message,
                    open: true,
                    severity: "danger",
                });
            }
        } catch (error) {
        if (error.response) {
            if (error.response.status === 422) {
                    // Extract validation error messages
                    const errors = error.response.data.errors || {};
                    const errorMessages = Object.values(errors).flat().join(", ");

                    setNotif({
                        notifMsg: `Validation failed: ${errorMessages}`,
                        open: true,
                        severity: "warning",
                    });
                    return;
                }
            }
            else{
                setNotif({
                    notifMsg: "Something went wrong with the server",
                    open: true,
                    severity: "danger",
                });
            }
        }
    };

    const toggleAddingContact = () => {
        // Toggle the state to show/hide the contact input table
        setIsAddingContact(!isAddingContact);
    };
    const openDeleteModal = (id) => {
        setModalDelete({
            id: id,
            notifMsg: "Are you sure you want to delete this item?",
            open: true,
            severity: "danger",
        });
    };
    const onDelete = async (contactId) => {
        try {
            // Implement the logic to delete the contact using the contactId
            await api.delete(`tms/companies/delete-contact/${contactId}`, {});
            await getCompanyData(selectedCompany);
            // After successful deletion, you can update the contact list or take other actions as needed
        } catch (error) {
            // Handle errors if the deletion fails
            setNotif({
                notifMsg: "Failed to delete contact",
                open: true,
                severity: "danger",
            });
        }
    };

    // Render the table rows with editable fields for contacts
    const contactRows = contacts.map((data, key) => (
        <tr key={key}>
            <td>
                <Controller
                    name="company_contact_person"
                    control={control}
                    render={({ field }) => (
                        <Form.Check
                            type="radio"
                            label={key + 1}
                            {...field}
                            checked={field.value === data.id} // Check the radio button if the ID matches
                            onChange={() => {
                                setValue(field.name, data?.id);
                                setValue('default_contact_person', data.name)
                                setValue('default_contact_number', data.contact_number)
                            }}
                        />
                    )}
                />
            </td>
            <td>
                {editedContactId === data.id ? (
                    <Form.Control
                        type="text"
                        value={editedContactData?.name || ""}
                        onChange={(e) =>
                            setEditedContactData({
                                ...editedContactData,
                                name: e.target.value,
                            })
                        }
                    />
                ) : (
                    data.name
                )}
            </td>
            <td>
                {editedContactId === data.id ? (
                    <Form.Control
                        type="text"
                        value={editedContactData?.contact_number || ""}
                        onChange={(e) =>
                            setEditedContactData({
                                ...editedContactData,
                                contact_number: e.target.value,
                            })
                        }
                    />
                ) : (
                    data.contact_number
                )}
            </td>
            <td>
                {editedContactId === data.id ? (
                    <Form.Control
                        type="email"
                        value={editedContactData?.email || ""}
                        onChange={(e) =>
                            setEditedContactData({
                                ...editedContactData,
                                email: e.target.value,
                            })
                        }
                    />
                ) : (
                    data.email
                )}
            </td>
            {permission?.includes("COMPANY-EDIT") && (
                <td>
                    {editedContactId === data.id ? (
                        <>
                            <Button
                                variant="success"
                                size="sm"
                                onClick={() => handleSaveClick(data.id)}
                            >
                                <Save size="18" />
                            </Button>
                            <Button
                                variant="light"
                                size="sm"
                                onClick={handleCancelClick}
                            >
                                <X size="18" />
                            </Button>
                        </>
                    ) : (
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleEditClick(data)}
                        >
                            <FileMinus size="18" />
                        </Button>
                    )}
                    <Button
                        variant="danger"
                        size="sm"
                        onClick={() => openDeleteModal(data?.id)}
                    >
                        <Trash size="18" />
                    </Button>
                </td>
            )}
        </tr>
    ));

    return (
        <div>
            <label className="form-label">Contact persons </label>
            
            {defaultContactData && (
                <>
                <Row>
                    
                    <Col md={6}>
                        <Controller
                            name="default_contact_person"
                            control={control}
                            defaultValue={defaultContactData?.name}
                            render={({ field }) => (
                                <Form.Control
                                    type="text"
                                    {...field}
                                    placeholder="Defaul contact person"
                                    disabled
                                    // value={defaultContactData?.name}
                                />
                            )}
                        />
                    </Col>
                    <Col md={6}>
                        <Controller
                            name="default_contact_number"
                            control={control}
                            defaultValue={defaultContactData?.contact_number}
                            render={({ field }) => (
                                <Form.Control
                                    type="text"
                                    {...field}
                                    placeholder="Defaul contact number"
                                    disabled
                                    // value={defaultContactData?.name}
                                />
                            )}
                        />
                    </Col>
                </Row>
                    
                
                </>
            )}
            <br></br>
            {selectedCompany && (
                <Table responsive striped bordered>
                    <thead>
                        <tr>
                            <th>S/N</th>
                            <th>Name</th>
                            <th>Contact number</th>
                            <th>Email</th>
                            {permission?.includes("COMPANY-EDIT") && (
                                <th>Action</th>
                            )}
                        </tr>
                    </thead>
                    <tbody>{contactRows}</tbody>
                </Table>
            )}
            {isAddingContact ? (
                <Table responsive striped bordered>
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Contact number</th>
                            <th>Email</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>
                                <Form.Control
                                    type="text"
                                    placeholder="Name"
                                    value={newContact.name}
                                    onChange={(e) => {
                                        handleNewContactChange(
                                            "name",
                                            e.target.value
                                        );
                                    }}
                                />
                            </td>
                            <td>
                                <Form.Control
                                    type="text"
                                    placeholder="Contact Number"
                                    value={newContact.contact_number}
                                    onChange={(e) =>
                                        handleNewContactChange(
                                            "contact_number",
                                            e.target.value
                                        )
                                    }
                                />
                            </td>
                            <td>
                                <Form.Control
                                    type="email"
                                    placeholder="Email"
                                    value={newContact.email}
                                    onChange={(e) =>
                                        handleNewContactChange(
                                            "email",
                                            e.target.value
                                        )
                                    }
                                />
                            </td>
                            <td>
                                <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={handleAddNewContact}
                                >
                                    <Save size="18" />
                                </Button>
                                <Button
                                    variant="light"
                                    size="sm"
                                    onClick={toggleAddingContact}
                                >
                                    <X size="18" />
                                </Button>
                            </td>
                        </tr>
                    </tbody>
                </Table>
            ) : (
                selectedCompany && (
                    <Button variant="primary" onClick={toggleAddingContact}>
                        <Plus /> Add contact
                    </Button>
                )
            )}
            <DeleteModal
                modalInfo={modalDelete}
                setModalInfo={setModalDelete}
                api={onDelete}
            />
            {/* SnackbarAlert */}
            <SnackbarAlert notif={notif} setNotif={setNotif} />
        </div>
    );
};

export default ContactSection;
